'use client';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

import {
  LOCATION_MAP,
  ONBOARDING_SEQUENCE,
  type RegionName,
  TYPOGRAPHY_IMAGES,
} from '../constants';
import { Chip } from './Chip';
import { StampMapErrorFallback } from './ErrorFallback';
import { StampButton } from './StampButton';

const StampMapContent = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isLogin = useAuthStore((state) => !!state.isLogin);
  const [onboardingStep, setOnboardingStep] = useState(-1);
  const [onboardingCount, setOnboardingCount] = useState(0);
  const [showEmptyState, setShowEmptyState] = useState(false);

  const { data: localTourData } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/home-donation/event/local-tour'](),
    enabled: isLogin,
    select: ({ context }) => context,
  });

  const donationCount = localTourData?.donationCount ?? 0;
  const localDonationList = localTourData?.localDonationInfoList ?? [];

  const donationMap = new Map(
    localDonationList.map((item) => [item.localName, item]),
  );

  const locationsToShow: RegionName[] =
    donationCount >= 5
      ? (localDonationList.map((item) => item.localName) as RegionName[])
      : (Object.keys(LOCATION_MAP) as RegionName[]);

  useEffect(() => {
    if (!isInView || donationCount > 0) return;

    setShowEmptyState(false);

    const STEP_DURATION = 200;
    const PAUSE_DURATION = 500;
    const TOTAL_STEPS = ONBOARDING_SEQUENCE.length;

    let rafId: number;
    let startTime: number | null = null;
    let phase: 'up' | 'pause' | 'down' | 'complete' = 'up';

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      if (phase === 'up') {
        const currentStep = Math.floor(elapsed / STEP_DURATION);

        if (currentStep < TOTAL_STEPS) {
          setOnboardingStep(currentStep);
          setOnboardingCount(currentStep + 1);
          rafId = requestAnimationFrame(animate);
        } else {
          phase = 'pause';
          startTime = timestamp;
          rafId = requestAnimationFrame(animate);
        }
      } else if (phase === 'pause') {
        if (elapsed >= PAUSE_DURATION) {
          phase = 'down';
          startTime = timestamp;
          rafId = requestAnimationFrame(animate);
        } else {
          rafId = requestAnimationFrame(animate);
        }
      } else if (phase === 'down') {
        const currentStep = Math.floor(elapsed / STEP_DURATION);
        const downStep = TOTAL_STEPS - 1 - currentStep;

        if (downStep >= 0) {
          setOnboardingStep(downStep);
          setOnboardingCount(downStep + 1);
          rafId = requestAnimationFrame(animate);
        } else {
          phase = 'complete';
          setOnboardingStep(-1);
          setOnboardingCount(0);
          setShowEmptyState(true);
        }
      }
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isInView, donationCount]);

  return (
    <>
      <div id="my-stamp" className="relative top-[-68px]" aria-hidden="true" />
      <section className="w-full bg-B-25 pt-80" aria-labelledby="stamp-heading">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex flex-col items-center gap-6 pb-2"
        >
          <p className="font-700 text-Gray-800 typo-h5">
            지역 스탬프를 모아보세요!
          </p>
          <h2 id="stamp-heading" className="pb-16">
            <Image
              src={TYPOGRAPHY_IMAGES.stampHeading.url}
              alt={TYPOGRAPHY_IMAGES.stampHeading.alt}
              width={TYPOGRAPHY_IMAGES.stampHeading.width}
              height={TYPOGRAPHY_IMAGES.stampHeading.height}
              priority
              className="object-contain"
            />
          </h2>
          <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={
              isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }
            }
            transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
            className={cn('rounded-pill px-20 py-10 font-800 typo-body1', {
              'text-Gray-white bg-O-500':
                donationCount > 0 || onboardingCount > 0,
              'text-Gray-500 bg-Gray-300':
                donationCount === 0 && onboardingCount === 0,
            })}
            role="status"
            aria-live="polite"
          >
            기부 지역 스코어:{' '}
            <span aria-label={`10개 중 ${donationCount || onboardingCount}개`}>
              {donationCount > 0 ? (
                <CountUp start={0} end={donationCount} duration={2} />
              ) : (
                onboardingCount
              )}{' '}
              / 10
            </span>
          </motion.div>
        </motion.div>
        <div className="px-8 py-30 sm:px-20">
          <div
            className="relative mx-auto w-full max-w-[375px]"
            style={{ aspectRatio: '375/608' }}
            role="img"
            aria-label={`대한민국 지도에 표시된 기부 스탬프. 총 10개 중 ${donationCount}개 획득`}
          >
            <SvgStampMap />

            {locationsToShow.map((localName) => {
              const donationInfo = donationMap.get(localName);
              const onboardingIndex = ONBOARDING_SEQUENCE.indexOf(localName);
              const isOnboardingActive =
                donationCount === 0 &&
                onboardingStep >= 0 &&
                onboardingIndex >= 0 &&
                onboardingIndex <= onboardingStep;

              return (
                <Chip
                  key={localName}
                  className={LOCATION_MAP[localName].className}
                  localName={localName}
                  specialtyImageUrl={donationInfo?.specialtyImageUrl}
                  isActive={!!donationInfo || isOnboardingActive}
                  isRecent={donationInfo?.recentYN ?? false}
                />
              );
            })}

            {donationCount === 0 && showEmptyState && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-32"
                style={{ backdropFilter: 'blur(4px)' }}
                role="alert"
                aria-live="polite"
              >
                <svg
                  width="35"
                  height="86"
                  viewBox="0 0 35 86"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M0.23 16.18C-0.08 12.26 -0.08 4.1 0.23 0.17C3.39 -0.04 6.96 -0.04 9.81 0.07C26.13 0.69 35 9.26 35 25.17C35 35.6 29.8 43.45 20.62 46.96C20.62 50.68 20.42 54.705 20.11 58.22C16.24 58.63 6.86 58.63 3.19 58.22C2.47 49.64 2.27 39.32 2.68 35.7L6.45 35.39C14.3 34.77 17.56 30.74 17.56 25.27C17.56 19.49 13.49 16.39 8.18 16.18C5.53 16.08 3.29 16.18 0.23 16.18ZM1.35 75.67C1.35 69.89 5.94 65.34 11.65 65.34C17.36 65.34 21.95 69.89 21.95 75.67C21.95 81.46 17.36 86 11.65 86C5.94 86 1.35 81.46 1.35 75.67Z"
                    fill="url(#paint0_linear_23070_6590)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_23070_6590"
                      x1="-9.50001"
                      y1="4.0715e-06"
                      x2="35"
                      y2="92.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#FF8428" />
                      <stop offset="1" stopColor="#FF4800" />
                    </linearGradient>
                  </defs>
                </svg>
                <p
                  className="text-center text-[24px] font-700 leading-[1.3] tracking-[-0.75px]"
                  style={{
                    background:
                      'linear-gradient(180deg, #FF7C34 0%, #FF5C00 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  지금 바로 첫 번째 스탬프를
                  <br />
                  찍어보세요!
                </p>
              </motion.div>
            )}
          </div>
        </div>
        <div className="p-20">
          {donationCount >= 10 ? (
            <StampButton variant="orange" disabled>
              스탬프 모으기 완료!
            </StampButton>
          ) : (
            <StampButton
              variant="orange"
              href={`${DONATION_ROUTES.GIFTS.pathname}?utm_source=wello&utm_medium=event&utm_campaign=wellotour_gift&utm_content=donation_251106`}
            >
              {donationCount > 0 ? '답례품 더 받기' : '기부하러 가기'}
            </StampButton>
          )}
        </div>
      </section>
    </>
  );
};

const SvgStampMap = () => {
  return (
    <svg
      className="h-auto w-full"
      viewBox="0 0 335 559"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M211.582 13.47L55.37 96.33C49.19 99.6 47.12 107.449 50.86 113.355L80.485 160.067C84.25 166.01 82.12 173.907 75.87 177.147L40.865 195.298C34.51 198.593 32.43 206.683 36.42 212.63L82.99 282.151C85.71 286.215 85.7 291.525 82.95 295.571L7.0 407.408C4.54 411.041 4.25 415.732 6.26 419.637L20.64 447.599C23.17 452.528 28.75 455.08 34.136 453.773L187.23 416.642C188.156 416.417 189.105 416.304 190.058 416.304H233.224C235.386 416.304 237.507 415.72 239.364 414.614L287.684 385.835C290.095 384.399 291.927 382.163 292.86 379.516L327.762 280.545C329.498 275.622 324.842 270.843 319.875 272.449C315.73 273.79 311.507 270.624 311.632 266.269L314.273 174.47C314.336 172.28 313.798 170.113 312.717 168.207L227.644 18.15C224.44 12.499 217.321 10.42 211.582 13.47Z"
        fill="white"
      />
      <path
        d="M41.78 489.575L83.77 480.75C86.08 480.266 88.48 480.61 90.55 481.724L121.684 498.419C127.1 501.324 128.639 508.381 124.924 513.277L106.482 537.58C104.944 539.607 102.691 540.971 100.183 541.395L46.94 550.398C44.64 550.787 42.27 550.36 40.26 549.19L15.48 534.834C10.05 531.684 8.83 524.359 12.95 519.618L36.29 492.797C37.721 491.154 39.65 490.023 41.78 489.575Z"
        fill="white"
      />
      <mask
        id="mask0_23090_4723"
        maskUnits="userSpaceOnUse"
        x="4"
        y="12"
        width="325"
        height="443"
      >
        <path
          d="M211.582 13.47L55.37 96.33C49.19 99.6 47.12 107.449 50.86 113.355L80.485 160.067C84.25 166.01 82.12 173.907 75.87 177.147L40.865 195.298C34.51 198.593 32.43 206.683 36.42 212.63L82.99 282.151C85.71 286.215 85.7 291.525 82.95 295.571L7.0 407.408C4.54 411.041 4.25 415.732 6.26 419.637L20.64 447.599C23.17 452.528 28.75 455.08 34.136 453.773L187.23 416.642C188.156 416.417 189.105 416.304 190.058 416.304H233.224C235.386 416.304 237.507 415.72 239.364 414.614L287.684 385.835C290.095 384.399 291.927 382.163 292.86 379.516L327.762 280.545C329.498 275.622 324.842 270.843 319.875 272.449C315.73 273.79 311.507 270.624 311.632 266.269L314.273 174.47C314.336 172.28 313.798 170.113 312.717 168.207L227.644 18.15C224.44 12.499 217.321 10.42 211.582 13.47Z"
          fill="white"
        />
      </mask>
      <g mask="url(#mask0_23090_4723)">
        <path
          d="M134.498 51L183.765 151.745M183.765 151.745L143.559 197.023M183.765 151.745L260.214 186.27M143.559 197.023L72.21 169.29M143.559 197.023L155.529 239.512M331 163.631L260.214 186.27M260.214 186.27L207 276.5M207 276.5L306.083 397.381M207 276.5L167.5 282M207 276.5L155.529 239.512M72.21 292.5L167.5 282M167.5 282L159.5 365.106M167.5 282L155.529 239.512M159.5 365.106L39 342.5M159.5 365.106L167.5 450.5"
          stroke="#EBF8FF"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="9 9"
        />
      </g>
    </svg>
  );
};

export const StampMap = () => {
  return (
    <ErrorBoundary FallbackComponent={StampMapErrorFallback}>
      <StampMapContent />
    </ErrorBoundary>
  );
};
